import {env} from "../env/env";
import {
  DigitalSizes,
  SocialSizes,
  DesignSizes,
  DigiboardSizes,
  BUSINESS_TYPE_SINGLE,
  BUSINESS_TYPE_MULTIPLE,
  BUSINESS_TYPE_PERSONAL
} from "./constants";
import {DesignDto, LocationDto} from "../generated/dashboard-client";
import {OrderDtoStatusEnum, OrderItemDto} from "../generated/print-client";

export function tick(callback: () => void) {
  setTimeout(callback, 0);
}

export function ellipsis(value: string, count: number = 25) {
  return value.substring(0, count) + "...";
}

export function defaultSelectFilterOption(input: string, option: any) {
  return option.value.toLowerCase().indexOf(input.toLowerCase()) === 0;
}

export function defaultSelectFilterSort(one: any, two: any) {
  return one.value.toLowerCase().localeCompare(two.value.toLowerCase());
}

export function toLogin() {
  window.location.href = env.dashboardPath;
}

export function isIOS() {
  return (
    navigator.userAgent.match(/iPhone/i) || navigator.userAgent.match(/iPad/i)
  );
}

export function openInNewTab(link: string) {
  if (link) {
    const opened = window.open(link, "_blank");
    if (opened) {
      opened.focus();
    }
  }
}

export function viewFileInNewTabUsingBlob(
  blob: Blob,
  fileName: string,
  win?: Window
) {
  if (fileName.endsWith(".pdf")) {
    blob = new Blob([blob], {type: "application/pdf"});
  }

  const url = window.URL.createObjectURL(blob);

  if (isIOS() && win) {
    win.location.href = url;
  } else {
    const link = document.createElement("a");
    link.href = url;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    if (link.parentNode != null) {
      link.parentNode.removeChild(link);
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 10000);
    }
  }
}

export function getImagePath(key: string) {
  return !key || key.indexOf("https") > -1 ? key : `${env.s3Path}/${key}`;
}

const formatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export function formatPrice(amount: string | number, light: boolean = false) {
  const amountAsNumber =
    typeof amount === "string" ? parseFloat(amount) : amount;
  const formatted = formatter.format(amountAsNumber);
  return light ? formatted.replace(".00", "") : formatted;
}

export function getSizeInMB(file: File) {
  return Math.floor(file.size / (1024 * 1024));
}

export function getCookie(name: string) {
  const search = name + "=";
  let value = "";
  if (document.cookie.length > 0) {
    let offset = document.cookie.indexOf(search);
    if (offset !== -1) {
      offset += search.length;
      let end = document.cookie.indexOf(";", offset);
      if (end === -1) end = document.cookie.length;

      const unescape = window.unescape || window.decodeURI;
      value = unescape(document.cookie.substring(offset, end));
    }
  }
  return value;
}

export function isOldBrowser() {
  const {userAgent} = navigator;
  return (
    (userAgent.includes("Firefox/") &&
      parseInt(userAgent.split("Firefox/")[1]) <= 56) ||
    (userAgent.includes("Chrome/") &&
      parseInt(userAgent.split("Chrome/")[1]) <= 65) ||
    (userAgent.includes("Edge/") && parseInt(userAgent.split("Edge/")[1]) <= 18)
  );
}

export function getUSStates() {
  return [
    {key: "AL", value: "Alabama"},
    {key: "AK", value: "Alaska"},
    {key: "AZ", value: "Arizona"},
    {key: "AR", value: "Arkansas"},
    {key: "CA", value: "California"},
    {key: "CO", value: "Colorado"},
    {key: "CT", value: "Connecticut"},
    {key: "DE", value: "Delaware"},
    {key: "DC", value: "District Of Columbia"},
    {key: "FL", value: "Florida"},
    {key: "GA", value: "Georgia"},
    {key: "HI", value: "Hawaii"},
    {key: "ID", value: "Idaho"},
    {key: "IL", value: "Illinois"},
    {key: "IN", value: "Indiana"},
    {key: "IA", value: "Iowa"},
    {key: "KS", value: "Kansas"},
    {key: "KY", value: "Kentucky"},
    {key: "LA", value: "Louisiana"},
    {key: "ME", value: "Maine"},
    {key: "MD", value: "Maryland"},
    {key: "MA", value: "Massachusetts"},
    {key: "MI", value: "Michigan"},
    {key: "MN", value: "Minnesota"},
    {key: "MS", value: "Mississippi"},
    {key: "MO", value: "Missouri"},
    {key: "MT", value: "Montana"},
    {key: "NE", value: "Nebraska"},
    {key: "NV", value: "Nevada"},
    {key: "NH", value: "New Hampshire"},
    {key: "NJ", value: "New Jersey"},
    {key: "NM", value: "New Mexico"},
    {key: "NY", value: "New York"},
    {key: "NC", value: "North Carolina"},
    {key: "ND", value: "North Dakota"},
    {key: "OH", value: "Ohio"},
    {key: "OK", value: "Oklahoma"},
    {key: "OR", value: "Oregon"},
    {key: "PA", value: "Pennsylvania"},
    {key: "RI", value: "Rhode Island"},
    {key: "SC", value: "South Carolina"},
    {key: "SD", value: "South Dakota"},
    {key: "TN", value: "Tennessee"},
    {key: "TX", value: "Texas"},
    {key: "UT", value: "Utah"},
    {key: "VT", value: "Vermont"},
    {key: "VA", value: "Virginia"},
    {key: "WA", value: "Washington"},
    {key: "WV", value: "West Virginia"},
    {key: "WI", value: "Wisconsin"},
    {key: "WY", value: "Wyoming"},
  ];
}

export function getHeader(headers: string[][], key: string) {
  const found = headers.find((it) => it[0] === key);
  return found ? found[1] : "";
}

export function isDigital(design: DesignDto) {
  if (!design.size) return false;
  return DigitalSizes.indexOf(design.size) > -1;
}

export function isDigiboard(design: DesignDto) {
  if (!design.size) return false;
  return DigiboardSizes.indexOf(design.size) > -1;
}

export function isA4(design: DesignDto) {
  if (!design.size) return false;
  return design.size.indexOf('a4') > -1
}

export function isSocial(design: DesignDto) {
  if (!design.size) return false;
  return SocialSizes.indexOf(design.size) > -1;
}

export function isCustomSize(design: DesignDto) {
  if (!design.size) return false;
  return design.size === "custom";
}

export function getCookieValue(name: string) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : "";
}

export function prettifyPaperSize(paperSize: string) {
  return paperSize.replace('x', ' x ');
}

export function getPrintCartSizeLabel(designSize: string) {
  const data = DesignSizes[designSize ?? ""];
  if (data) {
    if (data[0] === "takeout") {
      return "folded takeout";
    } else {
      return data[0];
    }
  }
  return "";
}

export function getSizeLabel(designSize: string) {
  const data = DesignSizes[designSize ?? ""];
  return data ? data[0] : "";
}

export function getSizeDimensions(designSize: string) {
  const data = DesignSizes[designSize ?? ""];
  return data ? data[1] : "";
}

export function prettifyPaperType(paperType: string, item?: OrderItemDto) {
  let toReturn = paperType.replace('-', ' ');
  toReturn = toReturn.split(' ').map(it => it.charAt(0).toUpperCase() + it.slice(1)).join(' ');
  const data = DesignSizes[item?.designSize ?? ""] || "";
  if (data && data[0] === 'loyalty card') {
    toReturn += " - coated on one side";
  }
  if (paperType === 'synthetic' && data && data[0] === 'menu') {
    toReturn += " - includes rounded corners";
  }
  return toReturn;
}

export function getPaginationTitle(value: string) {
  switch (value) {
    case 'single-sided':
      return 'Single-sided';

    case 'double-sided':
      return 'Double-sided';

    case 'front-double-sided':
      return 'First Page + Double-sided Interior';

    case 'back-double-sided':
      return 'Last Page + Double-sided Interior';

    case 'front-back-double-sided':
      return 'First & Last Page + Double-sided Interior';

    default:
      return value;
  }
}

export function isAccountPath(path: string) {
  return path.indexOf("/account") > -1;
}

export function isPrintPath(path: string) {
  return path.indexOf("/print/checkout") > -1;
}

export function isLandingPath(path: string) {
  return path.indexOf("/account/register") > -1 ||
    path.indexOf("/account/login") > -1 ||
    path.indexOf("/account/sso") > -1 ||
    path.indexOf("/account/oauth") > -1 ||
    path.indexOf("/account/stream/v1/authorize") > -1 ||
    path.indexOf("/account/password") > -1;
}

export function isContentPath(path: string) {
  return path.indexOf("/content/") > -1;
}

export function isDSAPath(path: string) {
  return path.indexOf("/dsa/") > -1;
}

const EmailRegEx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(email: string) {
  return EmailRegEx.test(email);
}

export function isValidZip(zip: string): boolean {
  return !!zip;
}

export function hasPOBox1(address: string): boolean {
  if (!address) return false;
  const poBoxPatterns = [
    /\bp\.?\s*o\.?\s*box\b/i,
    /\bpost\s+office\s+box\b/i,
    /\bbox\s+\d+\b/i
  ];
  return poBoxPatterns.some(pattern => pattern.test(address));
}

export function hasPOBox2(address: string): boolean {
  return hasPOBox1(address);
}

const PhoneRegEx = /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/;

export function isValidPhone(phone: string) {
  return PhoneRegEx.test(phone);
}

export function getFriendlyOrderStatus(status: OrderDtoStatusEnum) {
  switch (status) {
    case OrderDtoStatusEnum.Paid:
    case OrderDtoStatusEnum.Sent:
    case OrderDtoStatusEnum.Processing:
      return 'Paid';

    case OrderDtoStatusEnum.Shipped:
      return 'Shipped';

    case OrderDtoStatusEnum.Delivered:
      return 'Delivered';

    case OrderDtoStatusEnum.Canceled:
      return 'Canceled';

    default:
      return status;
  }
}

export function getTemplateThumb(id: string, memberId: number) {
  return `https://timber.mhmcdn.com/public/member/${memberId}/thumb/${id}-w${env.rendererImageHeight}-p1.jpg`;
}

export function getRendererThumbPathWithPage(assetId: string, page: number) {
  return `${env.rendererPath}/design/thumbnail/${assetId}?width=${env.rendererImageHeight}&page=${page}`;
}

export function iniFrame() {
  return window.self !== window.top;
}

export function setSiteBannerCookie(banner: boolean) {
  const now = new Date();
  now.setTime(now.getTime() + 14 * 24 * 60 * 60 * 1000);
  const expiration = "expires=" + now.toUTCString();
  document.cookie = `showSiteBanner=${banner.toString()};${expiration};path=/`;
}

export function setExpandFoldersCookie(show: boolean) {
  const now = new Date();
  now.setTime(now.getTime() + 365 * 24 * 60 * 60 * 1000);
  const expiration = "expires=" + now.toUTCString();
  document.cookie = `expandFolders=${show.toString()};${expiration};path=/`;
}

export function setDesignSortCookie(sort: string) {
  const now = new Date();
  now.setTime(now.getTime() + 365 * 24 * 60 * 60 * 1000);
  const expiration = "expires=" + now.toUTCString();
  document.cookie = `designSort=${sort.toString()};${expiration};path=/`;
}

export function synchronousRequest(url: string) {
  const xhr = new XMLHttpRequest();
  xhr.open('GET', url, false);
  xhr.send(null);
  if (xhr.status === 200) {
    return xhr.responseText;
  } else {
    return '';
  }
}

export function getTimeAgo(design: DesignDto) {
  return getTimeAgoFromDate(new Date(design.updatedDate!));
}

export function getTimeAgoFromDate(date: Date) {
  const today = new Date();
  const dayDiff = Math.floor((today.getTime() - date.getTime()) / (1000 * 3600 * 24));
  if (dayDiff === 0) {
    return `${date.toLocaleTimeString([], {hour: "2-digit", minute: "2-digit"})} today`;
  } else if (dayDiff === 1) {
    return "yesterday";
  } else if (dayDiff < 7) {
    return `${dayDiff} days ago`;
  } else {
    const weeks = Math.floor(dayDiff / 7);
    if (weeks === 1) {
      return "1 week ago";
    } else {
      return `${weeks} weeks ago`;
    }
  }
}

export function getTimeAgoFromString(d: string) {
  const date = new Date(d);
  const today = new Date();
  const dayDiff = Math.floor((today.getTime() - date.getTime()) / (1000 * 3600 * 24));
  if (dayDiff === 0) {
    return `${date.toLocaleTimeString([], {hour: "2-digit", minute: "2-digit"})} today`;
  } else if (dayDiff === 1) {
    return "yesterday";
  } else if (dayDiff < 7) {
    return `${dayDiff} days ago`;
  } else {
    const weeks = Math.floor(dayDiff / 7);
    if (weeks === 1) {
      return "1 week ago";
    } else {
      return `${weeks} weeks ago`;
    }
  }
}

export function getRestaurantTypeLabel(type: string) {
  if (type === BUSINESS_TYPE_SINGLE) return "single";
  if (type === BUSINESS_TYPE_MULTIPLE) return "multiple";
  if (type === BUSINESS_TYPE_PERSONAL) return "personal";
  return "other";
}

export function getToday() {
  return new Date();
}

export function getS3Url(path: string) {
  return "https://mhm-timber.s3.amazonaws.com/" + path;
}

export function isVideoPath(path?: string) {
  if (!path) return false;
  const ext = path.split(/[?#]/)[0].split(".").pop()?.toLowerCase();
  return ext === "mp4" || ext === "m4v" || ext === "webm" || ext === "mov";
}

export function generatePassword(length = 16) {
  const charset = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+-=[]{}|;:',.<>?";
  let password = "";
  for (let i = 0; i < length; i++) {
    password += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return password;
}

export function generateToken(length = 16) {
  const charset = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let password = "";
  for (let i = 0; i < length; i++) {
    password += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return password;
}

export function isValidPassword(pass: string) {
  return /^(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[~`*()_\-+={}[\]|:;'<>,.?/!@#$%^&])(?=.{8,})/.test(pass);
}

export function sortLocationsByName(locations: LocationDto[], key: string) {
  const data = [...locations];

  data.sort((a: LocationDto, b: LocationDto) => {
    if (!key) return 0;
    return key === "asc"
      ? (a.name ?? "").localeCompare(b.name ?? "")
      : (b.name ?? "").localeCompare(a.name ?? "");
  });

  return data;
}
